import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { motion, useScroll } from "framer-motion";
import { Reveal, MaskedLine, ScrollMaskedLine } from "@/components/Reveal";
import { InquiryTree, ExpandingNetwork } from "@/components/AboutVisuals";
import { usePageTitle } from "@/lib/usePageTitle";

function Eyebrow({ children, testid }) {
  return (
    <Reveal>
      <p className="text-xs tracking-[0.25em] uppercase text-fog" data-testid={testid}>
        {children}
      </p>
    </Reveal>
  );
}

/* One question branches into different outcomes as you scroll */
function OutcomesTransition() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  return (
    <section ref={ref} className="relative h-[200vh] bg-white" data-testid="outcomes-section">
      <div className="sticky top-20 mx-auto max-w-[1400px] px-6 md:px-12 py-4 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-4">
          <p className="text-xs tracking-[0.25em] uppercase text-fog">From one question</p>
          <p className="font-serif text-2xl md:text-3xl leading-[1.3] tracking-tight mt-6 max-w-sm">
            One inquiry can take different forms, depending on what the question requires.
          </p>
        </div>
        <div className="md:col-span-8">
          <InquiryTree progress={scrollYProgress} showLabels className="w-full" />
        </div>
      </div>
    </section>
  );
}







export default function About() {
  usePageTitle("About | Mentheris", "Mentheris is an independent company working across research, technology and practice to understand what change sets in motion and build from what we learn.");
  return (
    <main className="pt-24 md:pt-32" data-testid="about-page">
      {/* ---------------- HERO ---------------- */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 pb-16 md:pb-20">
        <div className="max-w-4xl">
          <h1 className="font-serif font-medium tracking-tighter leading-[1.0] text-5xl md:text-7xl" data-testid="about-headline">
            <MaskedLine delay={0.3}>Ideas begin with questions.</MaskedLine>
            <MaskedLine delay={0.5}>
              <span className="italic text-ember">What follows can take many forms.</span>
            </MaskedLine>
          </h1>
          <Reveal delay={0.9} className="mt-10 max-w-xl">
            <p className="text-base md:text-lg text-fog leading-relaxed">
              Mentheris works across research, technology and practice to understand emerging
              change, develop new ways of thinking about it, and build from what we learn.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ---------------- WHY MENTHERIS ---------------- */}
      <section className="bg-white" data-testid="why-section">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-14 md:py-16">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <Eyebrow testid="why-eyebrow">Why Mentheris</Eyebrow>
          </div>
          <div className="md:col-span-9">
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight max-w-3xl">
              <ScrollMaskedLine>Change rarely stays where it begins.</ScrollMaskedLine>
            </h2>
            <Reveal delay={0.15} className="mt-8 max-w-2xl space-y-5">
              <p className="text-fog leading-relaxed">
                A new technology may begin as a tool, but its effects move outward, changing how
                people work, how organisations operate, how knowledge develops, how decisions are
                made and what becomes possible next.
              </p>
              <p className="text-fog leading-relaxed">
                Mentheris was created to work within those wider transformations: to investigate
                them, make sense of them and, where useful, build new responses to what we learn.
              </p>
            </Reveal>
          </div>
        </div>
        </div>
      </section>

      {/* ---------------- WHAT WE ARE ---------------- */}
      <section className="bg-white" data-testid="what-section">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-14 md:py-16">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <Eyebrow testid="what-eyebrow">What we are</Eyebrow>
          </div>
          <div className="md:col-span-9">
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight">
              <ScrollMaskedLine>Research. Technology. Practice.</ScrollMaskedLine>
            </h2>
            <Reveal delay={0.15} className="mt-8 max-w-2xl">
              <p className="text-fog leading-relaxed">
                Mentheris is an independent company working across research, technology and
                practice. We investigate emerging relationships between technology and human
                systems, develop knowledge and frameworks from what we observe, collaborate with
                organisations and research ecosystems, and build technology products where ideas
                can become new forms of practice.
              </p>
            </Reveal>
            <div className="mt-14 text-center" data-testid="what-statement">
              <motion.p
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-15%" }}
                className="font-serif italic text-2xl md:text-4xl leading-[1.3] tracking-tight text-ink/85 max-w-3xl mx-auto"
              >
                {"Not every question leads to the same kind of".split(" ").map((w, i) => (
                  <motion.span
                    key={i}
                    className="inline-block mr-[0.28em]"
                    variants={{
                      hidden: { opacity: 0, y: 14, filter: "blur(6px)" },
                      visible: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } },
                    }}
                  >
                    {w}
                  </motion.span>
                ))}
                <motion.span
                  className="inline-block text-ember"
                  variants={{
                    hidden: { opacity: 0, y: 14, filter: "blur(6px)" },
                    visible: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.7, delay: 0.48, ease: [0.22, 1, 0.36, 1] } },
                  }}
                >
                  answer.
                </motion.span>
              </motion.p>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* ---------------- ONE QUESTION, DIFFERENT OUTCOMES ---------------- */}
      <OutcomesTransition />

      {/* ---------------- FOUNDER ---------------- */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-24 md:py-32" data-testid="founder-section">
        <div className="grid md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-4">
            <div className="relative mb-10 max-w-[300px]" data-testid="founder-portrait-frame">
              <div className="absolute -top-3 -left-3 right-3 bottom-3 border border-ember/40 pointer-events-none" aria-hidden="true" />
              <div className="group relative overflow-hidden border border-line">
                <img
                  src="/images/elena.jpg"
                  alt="Dr. Elena Puică, Founder & Principal Researcher"
                  className="w-full aspect-[3/4] object-cover saturate-[0.55] group-hover:saturate-100 group-hover:scale-[1.04] transition-all duration-700"
                  data-testid="founder-portrait"
                />
                <span
                  className="absolute inset-0 bg-ember/40 mix-blend-color opacity-100 group-hover:opacity-0 transition-opacity duration-700 pointer-events-none"
                  aria-hidden="true"
                />
                <span className="absolute bottom-4 right-4 w-2 h-2 rounded-full bg-ember" aria-hidden="true" />
              </div>
            </div>
            <p className="text-xs tracking-[0.25em] uppercase text-fog mb-6">Founder</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">Dr. Elena Puică</h2>
            <p className="mt-3 text-sm text-fog">Founder & Principal Researcher</p>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-8">
            <p className="font-serif text-2xl md:text-3xl leading-[1.3] tracking-tight max-w-3xl">
              Elena's work sits at the intersection of technology, organisational transformation
              and human systems.
            </p>
            <p className="mt-8 text-fog leading-relaxed max-w-2xl">
              Her background combines experience in complex enterprise technology and digital
              transformation with academic research in information systems and independent inquiry
              into the wider consequences of technological change.
            </p>
            <p className="mt-6 text-fog leading-relaxed max-w-2xl">
              She founded Mentheris as a place where these perspectives could meet, allowing
              research, practice and technology development to inform one another rather than
              remain separate.
            </p>
            <div className="mt-14" data-testid="founder-background">
              <p className="text-[10px] tracking-[0.3em] uppercase text-fog/70 mb-6">Background</p>
              <ul className="space-y-3">
                {[
                  "PhD · Economic Informatics",
                  "Enterprise technology & digital transformation",
                  "Research · Technology, organisations & human systems",
                  "Product & innovation development",
                ].map((line) => (
                  <li key={line} className="flex items-baseline gap-3 text-sm text-ink/80">
                    <span className="w-1.5 h-1.5 rounded-full bg-leaf shrink-0 translate-y-[-1px]" aria-hidden="true" />
                    {line}
                  </li>
                ))}
              </ul>
              <div className="mt-10 flex flex-wrap gap-8">
                <a
                  href="https://www.linkedin.com/company/mentheris/"
                  data-testid="founder-linkedin"
                  className="group inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-fog hover:text-ink transition-colors duration-300"
                >
                  Connect on LinkedIn
                  <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <Link
                  to="/research"
                  data-testid="founder-research-link"
                  className="group inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-fog hover:text-ink transition-colors duration-300"
                >
                  Read the research
                  <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- BUILT BY MENTHERIS ---------------- */}
      <section className="bg-forest text-bone" data-testid="products-section">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 pt-12 md:pt-16 pb-14 md:pb-16">
          <Reveal delay={0.2} className="mt-6">
            <div className="border border-bone/15 p-10 md:p-14 relative overflow-hidden group shadow-[inset_0_0_60px_rgba(110,155,130,0.05)]" data-testid="kaegora-block">
              <span className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-leaf/10 blur-2xl pointer-events-none" aria-hidden="true" />
              <p className="text-[10px] tracking-[0.3em] uppercase text-bone/50">
                Independent product · Developed by Mentheris
              </p>
              <h3 className="font-serif text-5xl md:text-7xl tracking-tight mt-6" data-testid="kaegora-name">
                Kaegora
              </h3>
              <p className="mt-8 font-serif italic text-xl md:text-2xl leading-snug text-bone/90 max-w-2xl" data-testid="kaegora-tagline">
                Kaegora is where you find the right collaborator.
              </p>
              <div className="mt-6 max-w-2xl space-y-4" data-testid="kaegora-description">
                <p className="text-bone/60 leading-relaxed">
                  Most collaboration today still happens through cold messages, weak-tie
                  introductions, or luck. Kaegora replaces that with a structured, low-pressure
                  system: post what you're building, apply to what interests you, and evaluate
                  genuine fit before you ever commit.
                </p>
              </div>

              <div className="mt-12 flex flex-wrap items-center gap-x-10 gap-y-5">
                <a
                  href="https://kaegora.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="kaegora-cta"
                  className="group/k inline-flex items-center gap-3 border border-bone/25 text-bone px-8 py-4 text-sm tracking-wide hover:bg-bone hover:text-forest transition-colors duration-300"
                >
                  Visit Kaegora
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover/k:translate-x-1 group-hover/k:-translate-y-1" />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>


      {/* ---------------- CONNECT ---------------- */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 pt-16 md:pt-20 pb-24 md:pb-32" data-testid="connect-section">
        <Eyebrow testid="connect-eyebrow">Connect</Eyebrow>
        <div className="grid md:grid-cols-12 gap-10 mt-6">
          <div className="md:col-span-9">
            <h2 className="font-serif text-2xl md:text-3xl lg:text-[32px] leading-[1.25] tracking-tight" data-testid="connect-heading">
              <ScrollMaskedLine className="lg:whitespace-nowrap">Questions can become research, collaborations,</ScrollMaskedLine>
              <ScrollMaskedLine delay={0.12}>
                <span className="italic text-ember">or something we haven't built yet.</span>
              </ScrollMaskedLine>
            </h2>
            <Reveal delay={0.2} className="mt-6 max-w-xl">
              <p className="text-fog leading-relaxed">
                If your work intersects with the questions, technologies or systems Mentheris is
                exploring, start a conversation.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <a
                href="mailto:contact@mentheris.com"
                data-testid="connect-cta"
                className="group inline-flex items-center gap-3 bg-ink text-bone px-8 py-4 text-sm tracking-wide hover:bg-ember transition-colors duration-300 mt-10"
              >
                Start a conversation
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
